import React from "react";
import { formatDate } from "../../../../helper/dateTimeHelper";

interface TimelineEvent {
  id?: string;
  stage_name?: string;
  status?: string;
  candidate_message?: string;
  changed_by?: any;
  changed_at?: string;
  event?: string;
  description?: string;
  created_at?: string;
  created_by?: string;
}

interface CandidateTimelineProps {
  events: TimelineEvent[];
}

const toLabel = (str?: string) =>
  str
    ? str.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "—";


const getStageStyle = (stage?: string) => {
  const s = (stage || "").toLowerCase();
  if (s.includes("applied")) return { icon: "ti-file-plus", color: "#3b82f6", bg: "#eff6ff" };
  if (s.includes("screen")) return { icon: "ti-filter", color: "#8b5cf6", bg: "#f5f3ff" };
  if (s.includes("shortlist")) return { icon: "ti-star", color: "#f59e0b", bg: "#fffbeb" };
  if (s.includes("interview")) return { icon: "ti-calendar-event", color: "#0ea5e9", bg: "#f0f9ff" };
  if (s.includes("offer")) return { icon: "ti-gift", color: "#10b981", bg: "#f0fdf4" };
  if (s.includes("hired")) return { icon: "ti-circle-check", color: "#22c55e", bg: "#f0fdf4" };
  if (s.includes("reject")) return { icon: "ti-user-x", color: "#ef4444", bg: "#fef2f2" };
  if (s.includes("withdraw")) return { icon: "ti-arrow-back", color: "#94a3b8", bg: "#f8fafc" };
  if (s.includes("submit")) return { icon: "ti-send", color: "#0ea5e9", bg: "#f0f9ff" };
  if (s.includes("review")) return { icon: "ti-eye", color: "#8b5cf6", bg: "#f5f3ff" };
  if (s.includes("document")) return { icon: "ti-file-upload", color: "#10b981", bg: "#f0fdf4" };
  if (s.includes("note")) return { icon: "ti-note", color: "#6c757d", bg: "#f8fafc" };
  return { icon: "ti-circle-dot", color: "#94a3b8", bg: "#f1f5f9" };
};

const CandidateTimeline: React.FC<CandidateTimelineProps> = ({ events }) => {
  if (!events || events.length === 0) {
    return (
      <div className="text-center py-5 text-muted">
        <i className="ti ti-timeline-event d-block mb-2 opacity-25" style={{ fontSize: 48 }} />
        <p className="mb-0 fw-semibold">No activity recorded yet.</p>
      </div>
    );
  }

  return (
    <div className="position-relative" style={{ paddingLeft: 44 }}>
      
      <div
        className="position-absolute"
        style={{ left: 17, top: 20, bottom: 20, width: 2, background: "#e2e8f0", borderRadius: 2 }}
      />

      {events.map((event, idx) => {
        const stageName = event.stage_name || event.event || "";
        const { icon, color, bg } = getStageStyle(stageName);
        const label = toLabel(stageName);
        const msg = event.candidate_message || event.description || "";
        const timestamp = event.changed_at || event.created_at || "";
        const changedBy = event.changed_by;
        const byLabel =
          typeof changedBy === "object" && changedBy !== null
            ? changedBy.name || changedBy.email || String(changedBy.id)
            : typeof changedBy === "number" || typeof changedBy === "string"
              ? String(changedBy)
              : "";

        const isFirst = idx === 0;
        const isLast = idx === events.length - 1;

        return (
          <div
            key={event.id || idx}
            className={`d-flex ${isLast ? "" : "mb-4"}`}
            style={{ position: "relative" }}
          >
            
            <div
              className="position-absolute d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
              style={{
                left: -44,
                top: 10,
                width: 36,
                height: 36,
                background: bg,
                border: `2px solid ${color}40`,
                zIndex: 2,
                boxShadow: isFirst ? `0 0 0 3px ${color}20` : "none",
              }}
            >
              <i className={`ti ${icon}`} style={{ fontSize: 17, color }} />
            </div>

            
            <div
              className="w-100 rounded-3 p-3"
              style={{
                background: "#fff",
                border: "1px solid #e8eaf0",
                borderLeft: `3px solid ${color}`,
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
              }}
            >
              <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
                <span className="fw-semibold text-dark" style={{ fontSize: 14 }}>
                  {label}
                </span>
                {timestamp && (
                  <span className="text-muted flex-shrink-0" style={{ fontSize: 11 }}>
                    {formatDate(timestamp)}
                  </span>
                )}
              </div>

              <div className="d-flex align-items-center gap-2 mt-1 flex-wrap">
                {msg && (
                  <p className="mb-0 text-muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
                    {msg}
                  </p>
                )}
                
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CandidateTimeline;